if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          viewerPrevBtn: this.querySelector('[id^="GalleryViewer"] .slider-button--prev'),
          viewerNextBtn: this.querySelector('[id^="GalleryViewer"] .slider-button--next'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Desktop thumbnail layouts + mobile color gallery (SliderComponent breaks when only .is-active is visible)
        const captureNavClicks = this.dataset.colorGalleryEnabled === 'true';
        if (this.elements.viewerPrevBtn) {
          this.elements.viewerPrevBtn.addEventListener(
            'click',
            (e) => this.navigateViewer(-1, e),
            captureNavClicks ? { capture: true } : false
          );
        }
        if (this.elements.viewerNextBtn) {
          this.elements.viewerNextBtn.addEventListener(
            'click',
            (e) => this.navigateViewer(1, e),
            captureNavClicks ? { capture: true } : false
          );
        }

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 200));
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

      }

      getActiveOrVisibleSlide() {
        return (
          this.elements.viewer?.querySelector('.slider__slide.is-active') || this.getVisibleSlide()
        );
      }

      getVisibleSlides() {
        const slides = this.elements.viewer?.querySelectorAll('.slider__slide[data-media-id]');
        if (!slides?.length) return [];
        return Array.from(slides).filter((slide) => !slide.classList.contains('product__media-item--color-hidden'));
      }

      getVisibleSlide() {
        const slides = this.getVisibleSlides();
        if (!slides.length) return null;

        const slider = this.elements.viewer.slider;
        if (!slider) return slides[0];

        const sliderCenter = slider.scrollLeft + slider.clientWidth / 2;
        let closest = slides[0];
        let minDistance = Infinity;

        slides.forEach((slide) => {
          const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
          const distance = Math.abs(slideCenter - sliderCenter);
          if (distance < minDistance) {
            minDistance = distance;
            closest = slide;
          }
        });

        return closest;
      }

      syncActiveSlide(activeSlide) {
        if (!activeSlide) return;

        this.elements.viewer.querySelectorAll('.slider__slide[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === activeSlide);
        });
      }

      navigateViewer(step, event) {
        const colorGallery = this.dataset.colorGalleryEnabled === 'true';
        const desktopThumbnailNav =
          this.mql.matches && this.dataset.desktopLayout.includes('thumbnail');

        // Mobile: native SliderComponent scroll; skip unless color gallery (only one slide has layout width)
        if (!colorGallery && !desktopThumbnailNav) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        
        const activeMedia =
          this.elements.viewer.querySelector('.slider__slide.is-active:not(.product__media-item--color-hidden)') ||
          this.getVisibleSlides()[0];
        if (!activeMedia) return;

        const allMedia = this.getVisibleSlides();
        let currentIndex = allMedia.indexOf(activeMedia);
        
        if (currentIndex === -1) currentIndex = 0;
        
        let newIndex = currentIndex + step;
        if (newIndex >= allMedia.length) newIndex = 0;
        if (newIndex < 0) newIndex = allMedia.length - 1;
        
        const nextMediaId = allMedia[newIndex].dataset.mediaId;
        // prepend=false to prevent DOM rearrangement and breaking native Shopify animations/order
        this.setActiveMedia(nextMediaId, false);
      }

      onSlideChanged(event) {
        const currentMedia = event.detail.currentElement;
        
        // Ensure the new active media gets the 'is-active' class natively without prepend
        this.elements.viewer.querySelectorAll('.slider__slide[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        currentMedia.classList.add('is-active');
        currentMedia.classList.remove('scroll-trigger', 'animate--fade-in', 'scroll-trigger--cancel');

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${currentMedia.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }

        if (!this.mql.matches) {
          this.playActiveMedia(currentMedia);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `.slider__slide[data-media-id="${mediaId}"]:not(.product__media-item--color-hidden)`
          ) || this.getVisibleSlides()[0];
        if (!activeMedia) {
          return;
        }

        // Prevent moving DOM elements from resetting their animations and hiding them
        activeMedia.classList.remove('scroll-trigger', 'animate--fade-in', 'scroll-trigger--cancel');

        this.elements.viewer.querySelectorAll('.slider__slide[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;

        deferredMedia.loadContent(false);
        this.ensureVideoPlayback(deferredMedia);
      }

      ensureVideoPlayback(deferredMedia) {
        const playVideo = () => {
          const video = deferredMedia.querySelector('video');
          if (!video) return;

          video.muted = true;
          video.loop = true;
          video.controls = false;
          video.playsInline = true;
          video.setAttribute('muted', '');
          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');

          const playPromise = video.play();
          if (playPromise?.catch) playPromise.catch(() => {});
        };

        playVideo();
        [100, 400].forEach((delay) => window.setTimeout(playVideo, delay));
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
